import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {FieldType} from '@ngx-formly/core';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import moment from 'moment';
import {BehaviorSubject, Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {ConfiguratorProductService} from '@app/product/configurator/services/configurator-product.service';
import {TransactionTypeService} from '@app/services/transactionTypes/transaction-type.service';
import {ContractDuration, DcpPriceData, DcpStartDatePeriod, MomentService, ValidationService} from '@dcp/configurator';
import {DcpConfiguratorFormService} from '@app/product/configurator/services/dcp-configurator-form.service';
import {DatePickerViewModel} from '@app/product/models/date-picker-view.model';
import {ContractDurationService} from '@app/services/contract-duration-service';
import {DcpCartDecoratorService} from '@app/services/dcp-cart-decorator.service';
import {ActiveCartFacade} from '@spartacus/cart/base/root';

@UntilDestroy()
@Component({
  selector: 'dcp-field-date-picker',
  templateUrl: './formly-field-date-picker.component.html',
  styleUrls: ['./formly-field-date-picker.component.scss'],
})
export class FormlyFieldDatePickerComponent extends FieldType implements OnInit {
  public dates: DatePickerViewModel = {};
  public showErrorLocal: boolean = false;
  public isDatepickerDisabled$ = new BehaviorSubject(false);
  private selectedContractDuration: ContractDuration;
  private priceData: DcpPriceData;
  public dateFormat: string = 'MMM DD, yyyy';
  public dateFormatISO: string = 'YYYY-MM-DD';

  endDateTooltipContent$: Observable<string>;

  constructor(
    private dcpCartDecoratorService: DcpCartDecoratorService,
    public momentService: MomentService,
    public configuratorFormService: DcpConfiguratorFormService,
    private cd: ChangeDetectorRef,
    public validationService: ValidationService,
    private transactionTypeService: TransactionTypeService,
    private configuratorProductService: ConfiguratorProductService,
    private activeCartFacade: ActiveCartFacade,
    private contractDurationService: ContractDurationService
  ) {
    super();
  }

  get minDate(): string {
    return this.getDateRestrictions('startDate');
  }

  get maxDate(): string {
    return this.getDateRestrictions('endDate');
  }

  ngOnInit(): void {
    this.resetValidators();
    this.isDatepickerDisabled$.next(true);
    this.subscribeToPriceData();
    this.subscribeToContractDuration();
    this.endDateTooltipContent$ = this.getEndDateTooltipContent$();
  }

  subscribeToPriceData(): void {
    this.dcpCartDecoratorService.priceDataSubject
      .pipe(untilDestroyed(this))
      .subscribe(priceData => {
        this.priceData = priceData;
        if (priceData?.configuration?.contract?.startDatePeriod) {
          this.dates = this.extractData(priceData);
          this.updateEndDate(priceData);
          this.isDatepickerDisabled$.next(false);
          this.setDatePickerValue(priceData.configuration.contract.startDatePeriod);
        } else {
          this.dates = {};
        }
        this.cd.detectChanges(); // needs to stay outside the if to keep the tests working
      });
  }

  private setDatePickerValue(startDatePeriod: DcpStartDatePeriod) {
    if (startDatePeriod.startDate &&
      (
        !this.formControl.value
        || this.momentService.ngbDateToMoment(this.formControl.value).isBefore(this.minDate)
        || this.momentService.ngbDateToMoment(this.formControl.value).isAfter(this.maxDate)
      )
    ) {
      this.formControl.setValue(this.momentService.stringToNgbDateStruct(startDatePeriod.startDate));
    }
  }

  extractData(priceData: DcpPriceData): DatePickerViewModel {
    return {
      startDatePeriod: {
        startDate: moment(priceData.configuration.contract.startDatePeriod.startDate),
        endDate: moment(priceData.configuration.contract.startDatePeriod.endDate),
      }
    };
  }

  getDateRestrictions(date: string): string {
    if (this.dates.startDatePeriod) {
      return this.dates.startDatePeriod[date].format(this.dateFormatISO);
    }
    return '';
  }

  /**
   * Replaces the validators used by formly with the angular validators.
   * https://angular.io/api/forms/Validators
   *
   * This is needed because formly uses a global FormGroup.
   * The validators of this FormGroup are not reset when the form is destroyed in the view.
   *
   * When more than on item is in cart and multiple items are edited after another,
   * the lowes max value of the quantity date-picker will be set as a validator.
   * It will not be overwritten by a higher max value.
   * This leads to a failing validation with a valid start date.
   */
  private resetValidators() {
    this.configuratorFormService.resetValidators(this.props, this.formControl, []);
  }

  getEndDateTooltipContent$() {
    return this.configuratorProductService.getVariant$().pipe(
      switchMap(vp => this.activeCartFacade.getEntry(vp.code)),
      map((cartEntry: OrderEntry) => this.transactionTypeService.getTransactionTypeCodeByUrlOrOrderEntry(cartEntry)),
      switchMap(ttCode => this.transactionTypeService.getTranslations(ttCode).pipe(map(translations => translations?.endDateTooltip)))
    );
  }

  /**
   * @private
   * Subscribes to the contract's duration service observable
   */
  private subscribeToContractDuration() {
    this.contractDurationService.contractDuration$
      .pipe(
        untilDestroyed(this)
      ).subscribe(contractDuration => {
      this.selectedContractDuration = contractDuration;
      this.updateEndDate(this.priceData);
    });
  }

  /**
   * @private
   * Updates the end date of the contract duration based on the start date
   * The start date will be either current date or the form value
   */
  private updateEndDate(priceData: DcpPriceData) {
    if (priceData?.price?.endDate) {
      this.dates.endDate = priceData.price.endDate;
      return;
    }

    if (!this.selectedContractDuration?.value || !this.formControl.value) {
      this.dates.endDate = undefined;
      return;
    }

    const startDate = this.formControl.value;
    const startDateMoment = this.momentService.ngbDateToMoment(startDate);
    this.dates.endDate = startDateMoment
      .subtract(1, 'day')
      .add(parseInt(this.selectedContractDuration?.value, 10), this.getUnitFromSelectedContractDuration())
      .format('YYYY-MM-DD');

  }

  /**
   * Gets a string representation of the contract duration metric
   */
  public getUnitFromSelectedContractDuration(): moment.unitOfTime.DurationConstructor {
    if (this.selectedContractDuration?.metric?.id === 'month') {
      return 'month';
    } else if (this.selectedContractDuration?.metric?.id === 'year') {
      return 'year';
    } else if (this.selectedContractDuration?.metric?.id === 'day') {
      return 'day';
    }
    return 'month';
  }

  public getFormattedMomentDate(): string {
    const value = this.formControl.value
    return value ? this.momentService.ngbDateToMoment(value).format(this.dateFormat) : ''
  }

  public onSelectionChange(event: CustomEvent): void {
    const stringDate = moment(event.detail.selectedValues[0]).format(this.dateFormatISO);
    this.formControl.setValue(this.momentService.stringToNgbDateStruct(stringDate));
  }

  public parseDateToFormat(date: string): string {
    return moment(date).format(this.dateFormat);
  }
}
